import {
  OrgCaps,
  PerOrgLimit,
  ProgramConfig,
  ProgramStat,
  UserOwningOrganization,
} from '@cibo/core'
import { flatten } from 'ramda'

export const programOrgLimits = (
  orgCaps?: Record<number, OrgCaps>,
  orgHeirarchy?: UserOwningOrganization,
  perOrgLimit?: PerOrgLimit
) =>
  orgCaps &&
  flatten(
    Object.keys(orgCaps).map(orgId => {
      const orgName = orgHeirarchy
        ? orgHeirarchy.id === orgId.toString()
          ? orgHeirarchy.label
          : orgHeirarchy.children &&
            orgHeirarchy.children.find(org => org.id === orgId.toString())?.label
        : ''
      return Object.keys(orgCaps[parseInt(orgId)]).map(limit => ({
        name: orgName,
        maxValue: orgCaps[parseInt(orgId)][limit as 'maxAcres' | 'maxFields'],
        committedAgainstLimit:
          perOrgLimit && perOrgLimit[parseInt(orgId)]
            ? perOrgLimit[parseInt(orgId)].committed[limit === 'maxAcres' ? 'acres' : 'fields']
            : 0,
        unit: limit,
        orgId,
      }))
    })
  )

export const parseEnrollmentNumbers = ({
  orgLimits,
  program,
  programStats,
  orgId,
}: {
  program: ProgramConfig
  programStats: ProgramStat
  orgLimits?: {
    name?: string
    maxValue?: number
    committedAgainstLimit?: number
    unit?: string
    orgId?: string
  }[]
  orgId?: string
}) => {
  const isInOrg = orgId !== 'root'
  const orgLimit =
    isInOrg && orgLimits && orgLimits[0] && orgLimits[0].orgId === orgId && orgLimits[0].maxValue

  const limitUnit: 'fields' | 'acres' =
    (Object.keys(programStats.globalLimit.max)[0] as 'fields' | 'acres') || 'acres'
  const globalLimit = programStats.globalLimit.max[limitUnit] as number
  const isAcres = limitUnit === 'acres'
  const isFields = limitUnit === 'fields'

  const enrolledCount = programStats.committed[limitUnit]
  const enrolledPercent = Math.round((enrolledCount / globalLimit) * 100)
  const globallyEnrolledCount = programStats.globalLimit.committed[limitUnit]
  const inProgressCount =
    program.opportunitiesByWfStatus?.inWorkflow[0][
      limitUnit === 'acres' ? 'acres' : 'numResources'
    ] ?? 0
  const inProgressPercent = inProgressCount ? Math.round((inProgressCount / globalLimit) * 100) : 0
  const globallyRemainingCount = globalLimit - globallyEnrolledCount
  const orgTargetReducedBy = orgLimit
    ? globallyRemainingCount > orgLimit
      ? 0
      : orgLimit - globallyRemainingCount - enrolledCount
    : 0
  const orgTargetReducedByPercent = orgLimit ? Math.round((orgTargetReducedBy / orgLimit) * 100) : 0
  const availableNumber =
    isInOrg && orgLimit
      ? orgLimit - enrolledCount - orgTargetReducedBy
      : globalLimit - globallyEnrolledCount - orgTargetReducedBy

  return {
    availableNumber,
    enrolledCount,
    enrolledPercent,
    globalLimit,
    globallyEnrolledCount,
    globallyRemainingCount,
    inProgressCount,
    inProgressPercent,
    isAcres,
    isFields,
    orgTargetReducedBy,
    orgTargetReducedByPercent,
    pipelineCount: inProgressCount + enrolledCount || 1,
    inOrgWithoutLimit: isInOrg && !orgLimit,
  }
}
